import { create } from "zustand";
import { persist } from "zustand/middleware";
import { AuthChangeEvent, User } from "@supabase/supabase-js";
import supabase from "../../utils/supabase";
import { fetchWithApiError } from "../utils/analyticsFetch";
import { authenticatedFetch } from "../utils/authFetch";
import { getUserConfirmedAt } from "../utils/authUser";
import { useProgressStore } from "./progressStore";

const API_BASE_URL = import.meta.env.VITE_BACKEND_API_URL;

interface UserState {
  user: User | null;
  isWhitelisted: boolean | null;
  isCheckingWhitelist: boolean;
  isAuthReady: boolean;
  isContentAdmin: boolean;
  isContentAdminReady: boolean;
  setUser: (user: User | null) => void;
  initializeUser: () => Promise<void>;
  checkWhitelistStatus: (email: string) => Promise<boolean>;
  refreshContentAdminAccess: () => Promise<boolean>;
}

const isOAuthUser = (user: User | null): boolean => {
  if (!user) return false;
  const provider = user.app_metadata?.provider;
  return provider === "google" || provider === "github";
};

const getMetadataString = (value: unknown): string =>
  typeof value === "string" ? value : "";

const getNameParts = (user: User): { firstName: string; lastName: string } => {
  const metadata = (user.user_metadata ?? {}) as Record<string, unknown>;
  const fullName =
    getMetadataString(metadata.full_name) || getMetadataString(metadata.name);
  const [firstFromFull, ...restFromFull] = fullName.trim().split(" ");
  const lastFromFull = restFromFull.join(" ").trim();

  const firstName =
    getMetadataString(metadata.first_name) || firstFromFull || "";
  const lastName =
    getMetadataString(metadata.last_name) || lastFromFull || "";

  return {
    firstName: firstName.trim(),
    lastName: lastName.trim(),
  };
};

const ensureUserRecord = async (user: User): Promise<void> => {
  if (!user.id || !user.email) return;

  try {
    const response = await fetchWithApiError(`${API_BASE_URL}/users/${user.id}`, undefined, {
      studentId: user.id,
    });
    if (response.ok) {
      return;
    }
    if (response.status !== 404) {
      console.warn("User lookup failed:", response.status);
      return;
    }

    const { firstName, lastName } = getNameParts(user);
    const createResponse = await fetchWithApiError(`${API_BASE_URL}/users/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: user.id,
        email: user.email,
        role: "student",
        first_name: firstName || "Student",
        last_name: lastName || "User",
      }),
    }, { studentId: user.id });

    if (!createResponse.ok && createResponse.status !== 400) {
      console.warn("User creation failed:", createResponse.status);
    }
  } catch (error) {
    console.error("User ensure failed:", error);
  }
};

const syncEmailVerification = async (user: User): Promise<void> => {
  const confirmedAt = getUserConfirmedAt(user);
  if (!confirmedAt || !user.id) return;

  try {
    await fetchWithApiError(
      `${API_BASE_URL}/users/${user.id}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email_verified: true,
          email_verified_at: confirmedAt,
        }),
      },
      { studentId: user.id }
    );
  } catch (error) {
    console.warn("Failed to sync email verification state:", error);
  }
};

const useUserStore = create<UserState>()(
  persist(
    (set, get) => {
      let lastUserId: string | null = null;
      let lastDeepSyncedUserId: string | null = null;
      let authSubscription: { unsubscribe: () => void } | null = null;
      let initializeUserInFlight: Promise<void> | null = null;

      const syncUserState = async (user: User | null): Promise<void> => {
        set({
          user,
          isContentAdmin: false,
          isContentAdminReady: !user,
        });

        if (user?.id && user.id !== lastUserId) {
          useProgressStore.getState().purgeOtherUsers(user.id);
        }
        lastUserId = user?.id ?? null;

        if (user) {
          await ensureUserRecord(user);
          await syncEmailVerification(user);
          if (isOAuthUser(user)) {
            set({ isWhitelisted: true, isCheckingWhitelist: false });
          } else if (user.email) {
            await get().checkWhitelistStatus(user.email);
          } else {
            set({ isWhitelisted: false, isCheckingWhitelist: false });
          }
        } else {
          set({ isWhitelisted: null, isCheckingWhitelist: false });
        }
        if (user) {
          void get().refreshContentAdminAccess();
        }
        lastDeepSyncedUserId = user?.id ?? null;
      };

      const syncUserStateLight = (user: User | null): void => {
        const currentUser = get().user;
        if (user?.id !== currentUser?.id) {
          set({ user });
        }

        if (user?.id && user.id !== lastUserId) {
          useProgressStore.getState().purgeOtherUsers(user.id);
        }
        lastUserId = user?.id ?? null;
      };

      return {
        user: null,
        isWhitelisted: null,
        isCheckingWhitelist: false,
        isAuthReady: false,
        isContentAdmin: false,
        isContentAdminReady: false,

        setUser: (user) => set({ user }),

        checkWhitelistStatus: async (email: string): Promise<boolean> => {
          try {
            set({ isCheckingWhitelist: true });
            const response = await fetchWithApiError(
              `${API_BASE_URL}/whitelist/check?email=${encodeURIComponent(email)}`,
              undefined,
              { studentId: get().user?.id }
            );
            if (!response.ok) {
              console.error("Whitelist check failed:", response.status);
              set({ isWhitelisted: false, isCheckingWhitelist: false });
              return false;
            }
            const data = await response.json();
            const isAllowed = data.is_allowed;
            set({ isWhitelisted: isAllowed, isCheckingWhitelist: false });
            return isAllowed;
          } catch (error) {
            console.error("Error checking whitelist:", error);
            set({ isWhitelisted: false, isCheckingWhitelist: false });
            return false;
          }
        },

        refreshContentAdminAccess: async (): Promise<boolean> => {
          if (!get().user) {
            set({ isContentAdmin: false, isContentAdminReady: true });
            return false;
          }

          set({ isContentAdminReady: false });
          try {
            const response = await authenticatedFetch(
              `${API_BASE_URL}/content-admin/me`
            );
            if (!response.ok) {
              set({ isContentAdmin: false, isContentAdminReady: true });
              return false;
            }
            const data = (await response.json()) as {
              is_content_admin?: boolean;
            };
            const isContentAdmin = data.is_content_admin === true;
            set({ isContentAdmin, isContentAdminReady: true });
            return isContentAdmin;
          } catch (error) {
            console.warn("Content administrator check failed:", error);
            set({ isContentAdmin: false, isContentAdminReady: true });
            return false;
          }
        },

        initializeUser: async () => {
          if (initializeUserInFlight) return initializeUserInFlight;

          const request = (async () => {
            set({ isAuthReady: false });

            try {
              const {
                data: { session },
                error,
              } = await supabase.auth.getSession();

              if (error) {
                throw error;
              }

              await syncUserState(session?.user ?? null);

              if (!authSubscription) {
                const {
                  data: { subscription },
                } = supabase.auth.onAuthStateChange(
                  async (event: AuthChangeEvent, nextSession) => {
                    try {
                      const nextUser = nextSession?.user ?? null;
                      const isSameDeepSyncedUser = Boolean(
                        nextUser?.id && nextUser.id === lastDeepSyncedUserId
                      );
                      const shouldUseLightSync =
                        isSameDeepSyncedUser &&
                        (event === "INITIAL_SESSION" ||
                          event === "TOKEN_REFRESHED" ||
                          event === "SIGNED_IN");

                      if (event === "SIGNED_OUT") {
                        await syncUserState(null);
                        return;
                      }

                      if (shouldUseLightSync) {
                        syncUserStateLight(nextUser);
                        return;
                      }

                      await syncUserState(nextUser);
                    } catch (syncError) {
                      console.error("Auth state sync failed:", syncError);
                    }
                  }
                );

                authSubscription = subscription;
              }
            } catch (error) {
              console.error("Failed to initialize auth state:", error);
              set({ isCheckingWhitelist: false });
            } finally {
              set({ isAuthReady: true });
            }
          })();

          initializeUserInFlight = request;

          try {
            await request;
          } finally {
            if (initializeUserInFlight === request) {
              initializeUserInFlight = null;
            }
          }
        },
      };
    },
    {
      name: "user-store",
      partialize: (state) => ({
        user: state.user,
        isWhitelisted: state.isWhitelisted,
      }),
    }
  )
);

export default useUserStore;
